import { Company } from '../types/company'
import { generateIndustryCode } from '../lib/industryCode'

// Deterministic pseudo-random based on seed
function seededRandom(seed: number) {
  let s = seed
  return () => {
    s = (s * 16807 + 0) % 2147483647
    return (s - 1) / 2147483646
  }
}

const rand = seededRandom(42)
const pick = <T,>(arr: T[]): T => arr[Math.floor(rand() * arr.length)]
const pickN = <T,>(arr: T[], n: number): T[] => {
  const shuffled = [...arr].sort(() => rand() - 0.5)
  return shuffled.slice(0, n)
}
const randInt = (min: number, max: number) =>
  Math.floor(rand() * (max - min + 1)) + min

// ── Name pools ──
const prefixes = [
  'Star',
  'Cloud',
  'Nova',
  'Peak',
  'Core',
  'Bright',
  'Deep',
  'Sky',
  'Ocean',
  'Thunder',
  'Crystal',
  'Quantum',
  'Fusion',
  'Apex',
  'Vertex',
  'Nexus',
  'Pulse',
  'Zenith',
  'Vanguard',
  'Horizon',
  'Matrix',
  'Prism',
  'Titan',
  'Atlas',
  'Orion',
  'Phoenix',
  'Dragon',
  'Jade',
  'Silk',
  'Bamboo',
  'Golden',
  'Silver',
  'Iron',
  'Steel',
  'Carbon',
  'Nano',
  'Micro',
  'Mega',
  'Ultra',
  'Hyper',
  'Smart',
  'Swift',
  'Rapid',
  'Prime',
  'Elite',
  'Alpha',
  'Beta',
  'Gamma',
  'Delta',
  'Sigma',
  'Blue',
  'Green',
  'Red',
  'White',
  'Black',
  'Amber',
  'Coral',
  'Azure',
  'Crimson',
  'Emerald',
]

const suffixes = [
  'Tech',
  'AI',
  'Robotics',
  'Systems',
  'Labs',
  'Science',
  'Digital',
  'Dynamics',
  'Solutions',
  'Intelligence',
  'Bio',
  'Pharma',
  'Genomics',
  'Medical',
  'Health',
  'Therapeutics',
  'Diagnostics',
  'Energy',
  'Power',
  'Solar',
  'Wind',
  'Green',
  'Clean',
  'Auto',
  'Motors',
  'Drive',
  'Mobility',
  'Transport',
  'Chip',
  'Semi',
  'Electronics',
  'Devices',
  'Circuits',
  'Space',
  'Aero',
  'Satellite',
  'Orbital',
  'Finance',
  'Pay',
  'Capital',
  'Ventures',
  'Cloud',
  'Data',
  'Net',
  'Link',
  'Connect',
  'Materials',
  'Optics',
  'Photonics',
  'Sensors',
]

const chinesePrefixes = [
  '星',
  '云',
  '新',
  '华',
  '中',
  '国',
  '天',
  '海',
  '龙',
  '凤',
  '金',
  '银',
  '铁',
  '钢',
  '碳',
  '纳',
  '微',
  '超',
  '极',
  '智',
  '蓝',
  '绿',
  '红',
  '白',
  '黑',
  '翠',
  '紫',
  '青',
  '明',
  '光',
  '深',
  '高',
  '远',
  '大',
  '广',
  '博',
  '达',
  '通',
  '联',
  '合',
  '创',
  '科',
  '信',
  '安',
  '泰',
  '瑞',
  '丰',
  '盛',
  '恒',
  '永',
  '鼎',
  '正',
  '方',
  '圆',
  '宏',
  '伟',
  '强',
  '胜',
  '优',
  '佳',
]

const chineseSuffixes = [
  '科技',
  '智能',
  '机器人',
  '系统',
  '实验室',
  '数字',
  '动力',
  '方案',
  '信息',
  '生物',
  '医药',
  '基因',
  '医疗',
  '健康',
  '诊断',
  '能源',
  '电力',
  '光伏',
  '风能',
  '绿色',
  '清洁',
  '汽车',
  '电机',
  '驱动',
  '出行',
  '运输',
  '芯片',
  '半导体',
  '电子',
  '器件',
  '电路',
  '航天',
  '卫星',
  '轨道',
  '金融',
  '支付',
  '资本',
  '云',
  '数据',
  '网络',
  '连接',
  '材料',
  '光学',
  '光子',
  '传感',
]

// ── Location pools ──
interface CityInfo {
  city: string
  province: string
}
const locations: CityInfo[] = [
  { city: 'Beijing', province: 'Beijing' },
  { city: 'Shanghai', province: 'Shanghai' },
  { city: 'Shenzhen', province: 'Guangdong' },
  { city: 'Guangzhou', province: 'Guangdong' },
  { city: 'Hangzhou', province: 'Zhejiang' },
  { city: 'Nanjing', province: 'Jiangsu' },
  { city: 'Chengdu', province: 'Sichuan' },
  { city: 'Wuhan', province: 'Hubei' },
  { city: 'Suzhou', province: 'Jiangsu' },
  { city: "Xi'an", province: 'Shaanxi' },
  { city: 'Hefei', province: 'Anhui' },
  { city: 'Changsha', province: 'Hunan' },
  { city: 'Chongqing', province: 'Chongqing' },
  { city: 'Tianjin', province: 'Tianjin' },
  { city: 'Dongguan', province: 'Guangdong' },
  { city: 'Foshan', province: 'Guangdong' },
  { city: 'Ningbo', province: 'Zhejiang' },
  { city: 'Zhengzhou', province: 'Henan' },
  { city: 'Qingdao', province: 'Shandong' },
  { city: 'Jinan', province: 'Shandong' },
  { city: 'Dalian', province: 'Liaoning' },
  { city: 'Shenyang', province: 'Liaoning' },
  { city: 'Harbin', province: 'Heilongjiang' },
  { city: 'Changchun', province: 'Jilin' },
  { city: 'Kunming', province: 'Yunnan' },
  { city: 'Xiamen', province: 'Fujian' },
  { city: 'Fuzhou', province: 'Fujian' },
  { city: 'Zhuhai', province: 'Guangdong' },
  { city: 'Wuxi', province: 'Jiangsu' },
  { city: 'Changzhou', province: 'Jiangsu' },
  { city: 'Nantong', province: 'Jiangsu' },
  { city: 'Wenzhou', province: 'Zhejiang' },
  { city: 'Guiyang', province: 'Guizhou' },
  { city: 'Nanning', province: 'Guangxi' },
  { city: 'Lanzhou', province: 'Gansu' },
  { city: 'Urumqi', province: 'Xinjiang' },
  { city: 'Hohhot', province: 'Inner Mongolia' },
  { city: 'Taiyuan', province: 'Shanxi' },
  { city: 'Shijiazhuang', province: 'Hebei' },
  { city: 'Nanchang', province: 'Jiangxi' },
  { city: 'Haikou', province: 'Hainan' },
  { city: 'Lhasa', province: 'Tibet' },
  { city: 'Yinchuan', province: 'Ningxia' },
  { city: 'Xining', province: 'Qinghai' },
  { city: 'Zhongshan', province: 'Guangdong' },
  { city: 'Huizhou', province: 'Guangdong' },
  { city: 'Jiaxing', province: 'Zhejiang' },
  { city: 'Yangzhou', province: 'Jiangsu' },
  { city: 'Luoyang', province: 'Henan' },
  { city: 'Zibo', province: 'Shandong' },
]

// ── Industry definitions ──
interface IndustryDef {
  industry: string
  subIndustries: string[]
  tags: string[][]
  products: string[][]
  descriptions: string[]
  logos: string[]
}

const industryDefs: IndustryDef[] = [
  {
    industry: 'Artificial Intelligence',
    subIndustries: [
      'Computer Vision',
      'NLP',
      'Machine Learning',
      'Generative AI',
      'AI Platform',
      'Speech Recognition',
      'Recommendation Systems',
      'AI Chips',
      'Edge AI',
      'AI Safety',
    ],
    tags: [
      ['Computer Vision', 'Deep Learning', 'Image Recognition'],
      ['NLP', 'Text Analytics', 'Sentiment Analysis'],
      ['AutoML', 'MLOps', 'Model Training'],
      ['Generative AI', 'LLM', 'Content Generation'],
      ['AI Platform', 'Enterprise AI', 'Data Science'],
      ['Speech Recognition', 'Voice AI', 'Conversational AI'],
      ['Recommendation', 'Personalization', 'User Modeling'],
      ['AI Chips', 'Neural Processing', 'Accelerators'],
      ['Edge AI', 'On-device AI', 'IoT Intelligence'],
      ['AI Safety', 'Alignment', 'Responsible AI'],
    ],
    products: [
      ['Vision Platform', 'Object Detection API', 'Video Analytics'],
      ['Text Engine', 'Language Model', 'Translation Service'],
      ['ML Pipeline', 'AutoML Suite', 'Feature Store'],
      ['GenAI Studio', 'Image Generator', 'Code Assistant'],
      ['AI Cloud', 'Model Hub', 'Training Platform'],
      ['Voice SDK', 'Speech-to-Text', 'Voice Cloning'],
      ['RecSys Engine', 'Personalization API', 'A/B Platform'],
      ['NPU Chip', 'AI Accelerator Card', 'Inference Engine'],
      ['Edge Runtime', 'TinyML Kit', 'Smart Sensor'],
      ['Safety Monitor', 'Bias Detector', 'Guardrails API'],
    ],
    descriptions: [
      'Develops cutting-edge AI solutions for enterprise and consumer applications using proprietary deep learning models.',
      'Builds intelligent systems that leverage machine learning to automate complex decision-making processes.',
      'Pioneers AI technology that transforms how businesses extract insights from unstructured data.',
      'Creates next-generation AI platforms enabling rapid deployment of machine learning models at scale.',
      'Specializes in developing AI-powered solutions for industry-specific challenges across multiple verticals.',
    ],
    logos: ['🤖', '🧠', '💡', '⚡', '🔮', '🎯', '📊', '🔬', '💻', '🌐'],
  },
  {
    industry: 'Electric Vehicles',
    subIndustries: [
      'Passenger EV',
      'Commercial EV',
      'EV Components',
      'Charging Infrastructure',
      'EV Software',
      'Micro Mobility',
      'Electric Trucks',
      'Electric Buses',
    ],
    tags: [
      ['Passenger EV', 'Sedan', 'SUV'],
      ['Commercial EV', 'Fleet', 'Logistics'],
      ['EV Motors', 'Power Electronics', 'Drivetrain'],
      ['Charging Stations', 'Fast Charging', 'V2G'],
      ['BMS', 'Vehicle OS', 'OTA Updates'],
      ['E-scooter', 'E-bike', 'Urban Mobility'],
      ['Electric Trucks', 'Heavy Duty', 'Long Haul'],
      ['Electric Bus', 'Public Transit', 'Zero Emission'],
    ],
    products: [
      ['EV Sedan', 'EV SUV', 'Smart Cockpit'],
      ['Electric Van', 'Delivery Vehicle', 'Fleet Manager'],
      ['Drive Motor', 'Inverter', 'Reducer'],
      ['DC Fast Charger', 'Home Charger', 'Charging App'],
      ['Battery Management System', 'Vehicle OS', 'Diagnostics'],
      ['Electric Scooter', 'E-bike Pro', 'Sharing Platform'],
      ['Electric Semi', 'Cargo EV', 'Route Optimizer'],
      ['City Bus EV', 'Coach EV', 'Transit Platform'],
    ],
    descriptions: [
      'Designs and manufactures innovative electric vehicles combining performance with sustainability.',
      'Develops next-generation EV technology focused on range, efficiency, and smart driving features.',
      'Builds electric mobility solutions for urban and commercial transportation needs.',
      'Creates comprehensive EV ecosystem including vehicles, charging, and software services.',
      'Pioneers affordable electric transportation to accelerate the transition to sustainable mobility.',
    ],
    logos: ['🚗', '🚙', '⚡', '🔋', '🚐', '🛵', '🚛', '🚌'],
  },
  {
    industry: 'Biotechnology',
    subIndustries: [
      'Genomics',
      'Drug Discovery',
      'Cell Therapy',
      'Diagnostics',
      'Synthetic Biology',
      'Bioinformatics',
      'Antibody Engineering',
      'Gene Therapy',
      'Microbiome',
      'Agricultural Biotech',
    ],
    tags: [
      ['Genomics', 'Sequencing', 'Precision Medicine'],
      ['Drug Discovery', 'Clinical Trials', 'Pharma'],
      ['CAR-T', 'Cell Therapy', 'Immunotherapy'],
      ['Diagnostics', 'Point-of-Care', 'Biomarkers'],
      ['Synthetic Biology', 'Metabolic Engineering', 'Biomanufacturing'],
      ['Bioinformatics', 'Computational Biology', 'Data Analytics'],
      ['Antibodies', 'Bispecific', 'ADC'],
      ['Gene Therapy', 'Gene Editing', 'CRISPR'],
      ['Microbiome', 'Probiotics', 'Gut Health'],
      ['Crop Science', 'Seed Tech', 'Biopesticides'],
    ],
    products: [
      ['Gene Sequencer', 'Genomic Database', 'Screening Panel'],
      ['Drug Candidate', 'Clinical Pipeline', 'Discovery Platform'],
      ['CAR-T Therapy', 'Cell Manufacturing', 'Immune Cell Bank'],
      ['Rapid Test Kit', 'PCR System', 'Biomarker Assay'],
      ['Bio-factory', 'Enzyme Library', 'Strain Engineering'],
      ['Analysis Platform', 'Variant Caller', 'Pathway Analyzer'],
      ['Monoclonal Antibody', 'Bispecific Platform', 'ADC Linker'],
      ['Gene Vector', 'Delivery System', 'Editing Tool'],
      ['Probiotic Formula', 'Microbiome Test', 'FMT Protocol'],
      ['Biotech Seeds', 'Growth Enhancer', 'Pest Resistance'],
    ],
    descriptions: [
      'Advances human health through innovative biotechnology solutions and breakthrough therapeutic approaches.',
      'Develops novel biological therapies targeting previously untreatable diseases.',
      'Leverages cutting-edge genomic technologies to enable precision medicine and personalized treatments.',
      'Creates diagnostic and therapeutic solutions using proprietary biotechnology platforms.',
      'Pioneers synthetic biology approaches to solve challenges in healthcare and sustainability.',
    ],
    logos: ['🧬', '🔬', '💊', '🧪', '🦠', '💉', '🧫', '🌱', '🔭', '⚗️'],
  },
  {
    industry: 'Semiconductors',
    subIndustries: [
      'Foundry',
      'Fabless Design',
      'Memory',
      'Analog IC',
      'Power Semiconductors',
      'MEMS',
      'EDA Tools',
      'Packaging',
      'Test Equipment',
      'Photonics',
    ],
    tags: [
      ['Foundry', 'Wafer Fab', 'Process Technology'],
      ['IC Design', 'SoC', 'ASIC'],
      ['DRAM', 'NAND', 'Emerging Memory'],
      ['Analog', 'Mixed Signal', 'RF'],
      ['Power IC', 'GaN', 'SiC'],
      ['MEMS', 'Sensors', 'Actuators'],
      ['EDA', 'Design Automation', 'Verification'],
      ['Advanced Packaging', 'Chiplet', '3D IC'],
      ['ATE', 'Probe Cards', 'Test Solutions'],
      ['Silicon Photonics', 'Optical IC', 'Transceivers'],
    ],
    products: [
      ['Wafer Fabrication', 'Process Node', 'Foundry Services'],
      ['Application Processor', 'IoT Chip', 'Custom SoC'],
      ['Memory Module', 'Flash Storage', 'Memory Controller'],
      ['ADC/DAC', 'Amplifier', 'RF Transceiver'],
      ['Power MOSFET', 'GaN Module', 'SiC Diode'],
      ['Accelerometer', 'Gyroscope', 'Pressure Sensor'],
      ['Design Suite', 'Simulator', 'Layout Tool'],
      ['Fan-out Package', 'Chiplet Interposer', '2.5D Package'],
      ['Test System', 'Handler', 'Probe Station'],
      ['Optical Module', 'Laser Chip', 'Photodetector'],
    ],
    descriptions: [
      'Designs and manufactures advanced semiconductor solutions for computing, communications, and IoT applications.',
      'Develops high-performance chip technology enabling next-generation electronic devices and systems.',
      'Creates innovative semiconductor products addressing growing demand for computing power and connectivity.',
      "Builds critical semiconductor infrastructure supporting China's technology self-sufficiency goals.",
      'Pioneers advanced chip architectures optimized for AI, 5G, and edge computing workloads.',
    ],
    logos: ['🔲', '💾', '🧮', '🖥️', '📡', '🔌', '⚙️', '🔧', '💻', '🔬'],
  },
  {
    industry: 'Renewable Energy',
    subIndustries: [
      'Solar PV',
      'Wind Power',
      'Energy Storage',
      'Hydrogen',
      'Smart Grid',
      'Geothermal',
      'Biomass',
      'Ocean Energy',
      'Carbon Capture',
      'Green Building',
    ],
    tags: [
      ['Solar', 'Photovoltaic', 'Modules'],
      ['Wind Turbines', 'Offshore Wind', 'Onshore Wind'],
      ['Battery Storage', 'Grid Storage', 'Residential Storage'],
      ['Green Hydrogen', 'Electrolyzer', 'Fuel Cell'],
      ['Smart Grid', 'Grid Management', 'Demand Response'],
      ['Geothermal', 'Heat Pump', 'Underground Energy'],
      ['Biomass', 'Biogas', 'Waste-to-Energy'],
      ['Tidal', 'Wave Energy', 'Marine Power'],
      ['Carbon Capture', 'CCS', 'Carbon Trading'],
      ['Green Building', 'BIPV', 'Energy Efficiency'],
    ],
    products: [
      ['Solar Panel', 'Solar Cell', 'Inverter'],
      ['Wind Turbine', 'Blade System', 'Generator'],
      ['Battery Pack', 'Storage System', 'Energy Management'],
      ['Electrolyzer', 'Fuel Cell Stack', 'H2 Storage'],
      ['Grid Controller', 'SCADA System', 'Microgrid'],
      ['Heat Pump System', 'Geothermal Probe', 'District Heating'],
      ['Biogas Plant', 'Biomass Boiler', 'Pellet System'],
      ['Tidal Turbine', 'Wave Converter', 'Marine Platform'],
      ['CO2 Capture Unit', 'Storage Well', 'Carbon Credit'],
      ['BIPV Module', 'Smart Window', 'Insulation System'],
    ],
    descriptions: [
      'Develops clean energy solutions to accelerate the global transition to renewable power.',
      'Manufactures high-efficiency renewable energy equipment for utility and distributed applications.',
      'Creates innovative energy storage and management systems for a sustainable future.',
      'Builds next-generation clean energy infrastructure supporting carbon neutrality goals.',
      'Pioneers advanced renewable energy technologies with focus on cost reduction and efficiency.',
    ],
    logos: ['☀️', '💨', '🔋', '⚡', '🌊', '🌍', '🌱', '♻️', '🔆', '🌅'],
  },
  {
    industry: 'Robotics',
    subIndustries: [
      'Industrial Robots',
      'Service Robots',
      'Medical Robots',
      'Agricultural Robots',
      'Underwater Robots',
      'Inspection Robots',
      'Delivery Robots',
      'Construction Robots',
    ],
    tags: [
      ['Industrial Automation', 'Manufacturing', 'Assembly'],
      ['Service Robot', 'Hospitality', 'Retail'],
      ['Surgical Robot', 'Rehabilitation', 'Medical Device'],
      ['Farm Robot', 'Harvesting', 'Precision Agriculture'],
      ['ROV', 'AUV', 'Marine Inspection'],
      ['Pipeline Inspection', 'Power Line', 'Infrastructure'],
      ['Last Mile Delivery', 'Autonomous Delivery', 'Logistics'],
      ['Construction Automation', '3D Printing', 'Demolition'],
    ],
    products: [
      ['Robot Arm', 'Welding Robot', 'Assembly Line'],
      ['Reception Robot', 'Cleaning Robot', 'Guide Robot'],
      ['Surgical System', 'Rehab Device', 'Care Robot'],
      ['Harvester Bot', 'Spraying Drone', 'Weeding Robot'],
      ['Underwater Drone', 'Subsea Inspector', 'Marine Sampler'],
      ['Crawler Robot', 'Flying Inspector', 'Sensor Array'],
      ['Delivery Bot', 'Cargo Drone', 'Locker System'],
      ['Bricklaying Robot', '3D Printer', 'Excavation Bot'],
    ],
    descriptions: [
      'Develops intelligent robotic systems that automate complex tasks across industries.',
      'Creates autonomous robots combining advanced AI with precision mechanical engineering.',
      'Builds next-generation robotic platforms for manufacturing, service, and specialized applications.',
      'Pioneers human-robot collaboration technology for safer and more productive workplaces.',
      'Designs versatile robotic solutions addressing labor shortages and efficiency challenges.',
    ],
    logos: ['🤖', '🦾', '🔧', '⚙️', '🏭', '📦', '🔬', '🚜'],
  },
  {
    industry: 'Fintech',
    subIndustries: [
      'Digital Payments',
      'InsurTech',
      'WealthTech',
      'RegTech',
      'Blockchain',
      'Lending Platform',
      'Neo Banking',
      'Crypto',
    ],
    tags: [
      ['Mobile Payments', 'QR Pay', 'Digital Wallet'],
      ['Insurance Tech', 'Claims AI', 'Underwriting'],
      ['Robo-advisor', 'Portfolio Management', 'Trading'],
      ['Compliance', 'KYC', 'AML'],
      ['Blockchain', 'DeFi', 'Smart Contracts'],
      ['P2P Lending', 'Credit Scoring', 'Microfinance'],
      ['Digital Bank', 'Mobile Banking', 'Open Banking'],
      ['Cryptocurrency', 'Digital Assets', 'Custody'],
    ],
    products: [
      ['Payment App', 'Merchant Terminal', 'Payment Gateway'],
      ['Insurance Platform', 'Claims Engine', 'Risk Model'],
      ['Investment App', 'Advisory Engine', 'Fund Platform'],
      ['Compliance Suite', 'Identity Verification', 'Risk Monitor'],
      ['Blockchain Platform', 'Token System', 'DApp Framework'],
      ['Lending Platform', 'Credit Engine', 'Collection System'],
      ['Banking App', 'Account System', 'Card Platform'],
      ['Exchange Platform', 'Wallet Service', 'Custody Solution'],
    ],
    descriptions: [
      'Transforms financial services through innovative technology and data-driven solutions.',
      'Builds next-generation financial infrastructure enabling faster, cheaper, and more inclusive services.',
      'Develops AI-powered financial technology platforms for banks, insurers, and asset managers.',
      'Creates seamless digital financial experiences for consumers and businesses.',
      'Pioneers financial innovation leveraging blockchain, AI, and big data analytics.',
    ],
    logos: ['💰', '🏦', '💳', '📈', '🔗', '💎', '🪙', '📊'],
  },
  {
    industry: 'Aerospace',
    subIndustries: [
      'Launch Vehicles',
      'Satellites',
      'Space Stations',
      'Satellite Internet',
      'Remote Sensing',
      'Space Tourism',
      'Propulsion',
      'Ground Systems',
    ],
    tags: [
      ['Rockets', 'Launch Services', 'Orbital'],
      ['Communication Satellite', 'Navigation', 'LEO'],
      ['Space Station', 'Habitat', 'Life Support'],
      ['Satellite Internet', 'Broadband', 'Constellation'],
      ['Earth Observation', 'SAR', 'Multispectral'],
      ['Suborbital', 'Space Tourism', 'Experience'],
      ['Rocket Engine', 'Ion Drive', 'Solid Motor'],
      ['Mission Control', 'Tracking', 'Telemetry'],
    ],
    products: [
      ['Launch Vehicle', 'Upper Stage', 'Fairing'],
      ['Comm Satellite', 'Nav Payload', 'Bus Platform'],
      ['Module', 'Docking System', 'ECLSS'],
      ['User Terminal', 'Gateway', 'Mesh Network'],
      ['Imaging Satellite', 'Data Platform', 'Analytics'],
      ['Capsule', 'Spaceplane', 'Training Center'],
      ['Engine', 'Thruster', 'Propellant System'],
      ['Ground Station', 'Antenna', 'Control Software'],
    ],
    descriptions: [
      'Develops commercial space technology to make space more accessible and economically viable.',
      "Builds satellite systems and launch capabilities supporting China's growing space economy.",
      'Creates innovative aerospace solutions spanning launch, satellite, and ground segments.',
      'Pioneers cost-effective space access through reusable launch vehicle technology.',
      'Designs advanced space systems for communications, earth observation, and scientific research.',
    ],
    logos: ['🚀', '🛰️', '🌟', '🔥', '🌙', '☄️', '🔭', '🌍'],
  },
  {
    industry: 'Technology',
    subIndustries: [
      'Consumer Electronics',
      'Enterprise Software',
      'Cybersecurity',
      'Cloud Services',
      'IoT',
      'AR/VR',
      'Gaming',
      'EdTech',
      'HealthTech',
      'AgriTech',
    ],
    tags: [
      ['Smartphones', 'Wearables', 'Audio'],
      ['SaaS', 'ERP', 'CRM'],
      ['Network Security', 'Endpoint Protection', 'Zero Trust'],
      ['IaaS', 'PaaS', 'Serverless'],
      ['Smart Home', 'Industrial IoT', 'Connected Devices'],
      ['AR Glasses', 'VR Headset', 'Metaverse'],
      ['Mobile Games', 'PC Games', 'Cloud Gaming'],
      ['Online Learning', 'LMS', 'Tutoring'],
      ['Telemedicine', 'Health Records', 'Wellness'],
      ['Precision Farming', 'Crop Monitoring', 'Farm Management'],
    ],
    products: [
      ['Smartphone', 'Smartwatch', 'Earbuds'],
      ['Business Suite', 'Collaboration Tool', 'Analytics Platform'],
      ['Firewall', 'SIEM', 'Identity Manager'],
      ['Cloud Platform', 'Container Service', 'Database Service'],
      ['Smart Hub', 'Sensor Kit', 'IoT Gateway'],
      ['AR Headset', 'VR Platform', 'Spatial Computing'],
      ['Game Title', 'Game Engine', 'Publishing Platform'],
      ['Learning Platform', 'Course Builder', 'Assessment Tool'],
      ['Health App', 'EMR System', 'Remote Monitor'],
      ['Farm Platform', 'Drone Service', 'Soil Analyzer'],
    ],
    descriptions: [
      'Creates innovative technology products and services that improve how people live and work.',
      'Develops enterprise technology solutions enabling digital transformation across industries.',
      'Builds consumer technology products combining cutting-edge innovation with intuitive design.',
      'Pioneers next-generation technology platforms for the connected digital economy.',
      'Delivers technology solutions that bridge the gap between innovation and practical application.',
    ],
    logos: ['📱', '💻', '🖥️', '⌚', '🎮', '🕶️', '📡', '🔐', '📚', '🌾'],
  },
  {
    industry: 'Cloud Computing',
    subIndustries: [
      'Public Cloud',
      'Private Cloud',
      'Hybrid Cloud',
      'CDN',
      'Database Services',
      'DevOps',
      'Serverless',
      'Edge Computing',
    ],
    tags: [
      ['Public Cloud', 'IaaS', 'Global'],
      ['Private Cloud', 'On-premise', 'Security'],
      ['Hybrid Cloud', 'Multi-cloud', 'Orchestration'],
      ['CDN', 'Content Delivery', 'Streaming'],
      ['Cloud Database', 'NoSQL', 'Time Series'],
      ['CI/CD', 'Container', 'Kubernetes'],
      ['FaaS', 'Event-driven', 'Microservices'],
      ['Edge Cloud', 'MEC', '5G Edge'],
    ],
    products: [
      ['Cloud Platform', 'Compute Instance', 'Object Storage'],
      ['Private Cloud Stack', 'Security Suite', 'Compliance Tools'],
      ['Cloud Manager', 'Migration Tool', 'Cost Optimizer'],
      ['CDN Service', 'Video Platform', 'Edge Cache'],
      ['Database Service', 'Data Warehouse', 'Cache Service'],
      ['Pipeline Tool', 'Registry', 'Monitoring Suite'],
      ['Function Service', 'API Gateway', 'Event Bus'],
      ['Edge Platform', 'Edge AI', 'IoT Edge'],
    ],
    descriptions: [
      'Provides scalable cloud computing infrastructure and services for businesses of all sizes.',
      'Builds enterprise-grade cloud platforms with focus on security, performance, and reliability.',
      'Delivers innovative cloud solutions enabling rapid digital transformation and modernization.',
      'Creates cloud-native technologies that power the next generation of applications and services.',
      'Develops specialized cloud services optimized for AI, big data, and real-time applications.',
    ],
    logos: ['☁️', '🌐', '💾', '🔧', '📊', '🔒', '⚡', '🌍'],
  },
  {
    industry: 'Autonomous Driving',
    subIndustries: [
      'L4 Robotaxi',
      'ADAS',
      'Autonomous Trucking',
      'Mining Automation',
      'Port Automation',
      'Simulation',
      'HD Mapping',
      'V2X',
    ],
    tags: [
      ['Robotaxi', 'Ride-hailing', 'L4'],
      ['ADAS', 'Highway Pilot', 'Parking'],
      ['Autonomous Trucks', 'Freight', 'Platooning'],
      ['Mining Vehicles', 'Off-road', 'Heavy Equipment'],
      ['Port AGV', 'Container', 'Terminal'],
      ['Driving Simulator', 'Scenario Testing', 'Digital Twin'],
      ['HD Map', 'Localization', 'Crowdsourcing'],
      ['V2X', 'C-V2X', 'Infrastructure'],
    ],
    products: [
      ['Robotaxi Fleet', 'Ride App', 'Safety Driver System'],
      ['Highway Assist', 'Auto Park', 'Traffic Jam Pilot'],
      ['Truck Autopilot', 'Fleet Manager', 'Route Planner'],
      ['Mining AV', 'Haul Truck System', 'Site Manager'],
      ['Port AGV', 'Crane Automation', 'Yard Management'],
      ['Sim Platform', 'Scenario Library', 'Testing Suite'],
      ['Map Platform', 'SLAM System', 'Update Service'],
      ['RSU', 'OBU', 'Traffic Signal System'],
    ],
    descriptions: [
      'Develops autonomous driving technology to make transportation safer and more efficient.',
      'Creates self-driving solutions for passenger, commercial, and industrial applications.',
      'Builds full-stack autonomous driving platforms combining perception, planning, and control.',
      'Pioneers autonomous mobility solutions for specific use cases and environments.',
      'Advances autonomous driving through innovative sensor fusion and AI algorithms.',
    ],
    logos: ['🚗', '🚛', '🚕', '🛣️', '🗺️', '📡', '🎮', '🏗️'],
  },
  {
    industry: 'Energy Storage',
    subIndustries: [
      'Lithium Batteries',
      'Sodium Batteries',
      'Solid State',
      'Flow Batteries',
      'Supercapacitors',
      'Battery Recycling',
      'BMS',
      'Grid Storage',
    ],
    tags: [
      ['Lithium-ion', 'LFP', 'NMC'],
      ['Sodium-ion', 'Low Cost', 'Abundant'],
      ['Solid State', 'Next Gen', 'High Energy'],
      ['Vanadium Flow', 'Long Duration', 'Grid Scale'],
      ['Supercapacitor', 'Fast Charge', 'High Power'],
      ['Battery Recycling', 'Circular Economy', 'Materials Recovery'],
      ['Battery Management', 'Safety', 'Monitoring'],
      ['Utility Storage', 'Peak Shaving', 'Frequency Regulation'],
    ],
    products: [
      ['Battery Cell', 'Battery Pack', 'Module'],
      ['Na-ion Cell', 'Energy Pack', 'Starter Battery'],
      ['SS Battery', 'Prototype Cell', 'Electrolyte'],
      ['Flow Battery Stack', 'Electrolyte Tank', 'Control System'],
      ['Supercap Module', 'Power Bank', 'Hybrid System'],
      ['Recycling Plant', 'Sorting System', 'Refining Process'],
      ['BMS Board', 'Cloud BMS', 'Safety Controller'],
      ['Container Storage', 'Rack System', 'Grid Inverter'],
    ],
    descriptions: [
      'Develops advanced energy storage solutions powering the transition to clean energy.',
      'Manufactures high-performance batteries for electric vehicles and grid applications.',
      'Creates next-generation energy storage technology with improved safety and performance.',
      'Builds comprehensive battery solutions from cell chemistry to system integration.',
      'Pioneers innovative energy storage approaches for a sustainable energy future.',
    ],
    logos: ['🔋', '⚡', '🔌', '💡', '♻️', '🔬', '⚙️', '🏭'],
  },
  {
    industry: 'Pharmaceuticals',
    subIndustry: [
      'Oncology',
      'Cardiology',
      'Neurology',
      'Immunology',
      'Rare Diseases',
      'Generics',
      'Traditional Chinese Medicine',
      'Vaccines',
    ],
    subIndustries: [
      'Oncology',
      'Cardiology',
      'Neurology',
      'Immunology',
      'Rare Diseases',
      'Generics',
      'Traditional Chinese Medicine',
      'Vaccines',
    ],
    tags: [
      ['Cancer Treatment', 'Targeted Therapy', 'Chemotherapy'],
      ['Heart Disease', 'Blood Pressure', 'Cholesterol'],
      ['CNS', "Alzheimer's", 'Depression'],
      ['Autoimmune', 'Inflammation', 'Allergy'],
      ['Orphan Drug', 'Gene Therapy', 'Enzyme Replacement'],
      ['Generic Drugs', 'Biosimilars', 'API'],
      ['TCM', 'Herbal Medicine', 'Acupuncture'],
      ['mRNA Vaccine', 'Inactivated Vaccine', 'Adjuvant'],
    ],
    products: [
      ['Anti-cancer Drug', 'Targeted Agent', 'Combination Therapy'],
      ['Antihypertensive', 'Statin', 'Anticoagulant'],
      ['Antidepressant', 'Neuroprotectant', 'Pain Reliever'],
      ['Anti-inflammatory', 'Immunosuppressant', 'Biologic'],
      ['Orphan Drug', 'Enzyme Therapy', 'Gene Vector'],
      ['Generic Tablet', 'Injectable', 'API Supply'],
      ['TCM Formula', 'Extract', 'Patch'],
      ['Vaccine Dose', 'Booster', 'Adjuvant System'],
    ],
    descriptions: [
      'Develops innovative pharmaceutical products addressing unmet medical needs.',
      'Creates breakthrough medicines through advanced drug discovery and development.',
      'Manufactures high-quality pharmaceutical products for domestic and global markets.',
      'Combines traditional Chinese medicine wisdom with modern pharmaceutical science.',
      'Builds a diverse pharmaceutical pipeline spanning multiple therapeutic areas.',
    ],
    logos: ['💊', '💉', '🏥', '🧪', '🔬', '🌿', '⚕️', '🩺'],
  },
]

// ── Leadership name pools ──
const lastNames = [
  'Wang',
  'Li',
  'Zhang',
  'Liu',
  'Chen',
  'Yang',
  'Huang',
  'Zhao',
  'Wu',
  'Zhou',
  'Xu',
  'Sun',
  'Ma',
  'Zhu',
  'Hu',
  'Guo',
  'He',
  'Lin',
  'Luo',
  'Zheng',
  'Liang',
  'Xie',
  'Song',
  'Tang',
  'Han',
  'Deng',
  'Feng',
  'Cao',
  'Peng',
  'Xiao',
  'Tian',
  'Dong',
  'Pan',
  'Yuan',
  'Jiang',
  'Cai',
  'Yu',
  'Du',
  'Ye',
  'Cheng',
  'Wei',
  'Su',
  'Lu',
  'Ding',
  'Ren',
  'Shen',
  'Yao',
  'Gu',
  'Qian',
  'Dai',
]

const firstNames = [
  'Wei',
  'Fang',
  'Lei',
  'Jun',
  'Jie',
  'Tao',
  'Ming',
  'Hua',
  'Qiang',
  'Ping',
  'Yan',
  'Xin',
  'Yong',
  'Hao',
  'Bo',
  'Chao',
  'Gang',
  'Bin',
  'Kai',
  'Peng',
  'Jian',
  'Feng',
  'Lin',
  'Rui',
  'Nan',
  'Wen',
  'Yi',
  'Zhi',
  'Xiang',
  'Long',
  'Dong',
  'Sheng',
  'Hong',
  'Guang',
  'Liang',
  'Cheng',
  'De',
  'Zhong',
  'Shan',
  'Hai',
]

const titles = [
  'CEO',
  'CTO',
  'COO',
  'CFO',
  'Chairman',
  'President',
  'Founder & CEO',
  'Co-founder & CEO',
  'Co-founder & CTO',
  'VP Engineering',
  'Chief Scientist',
  'VP R&D',
  'VP Product',
  'General Manager',
  'Managing Director',
]

const fundingRoundOptions = [
  'Seed',
  'Angel',
  'Pre-A',
  'Series A',
  'Series B',
  'Series C',
  'Series D',
  'Series E',
  'Series F',
  'IPO',
  'Pre-IPO',
  'Subsidiary',
  'Private',
  'Strategic',
]

const revenueRanges = [
  { min: 1, max: 10, unit: 'M' },
  { min: 10, max: 50, unit: 'M' },
  { min: 50, max: 200, unit: 'M' },
  { min: 200, max: 500, unit: 'M' },
  { min: 500, max: 999, unit: 'M' },
  { min: 1, max: 5, unit: 'B' },
  { min: 5, max: 20, unit: 'B' },
]

const fundingRanges = [
  '$5M',
  '$10M',
  '$15M',
  '$20M',
  '$30M',
  '$50M',
  '$80M',
  '$100M',
  '$150M',
  '$200M',
  '$300M',
  '$500M',
  '$800M',
  '$1B',
  '$1.5B',
  '$2B',
  '$3B',
  '$5B',
  'N/A',
]

// ── Generator ──
export function generateCompanies(count: number, startId: number): Company[] {
  const companies: Company[] = []
  const usedNames = new Set<string>()

  for (let i = 0; i < count; i++) {
    const id = (startId + i).toString()

    // Generate unique name
    let name = ''
    let attempts = 0
    do {
      const p = pick(prefixes)
      const s = pick(suffixes)
      name = `${p} ${s}`
      attempts++
      if (attempts > 50) {
        name = `${pick(prefixes)}${pick(prefixes)} ${pick(suffixes)}`
      }
    } while (usedNames.has(name))
    usedNames.add(name)

    const nameChinese = `${pick(chinesePrefixes)}${pick(chinesePrefixes)}${pick(chineseSuffixes)}`

    const industryDef = pick(industryDefs)
    const subIdx = randInt(0, industryDef.subIndustries.length - 1)
    const subIndustry = industryDef.subIndustries[subIdx]
    const tagPool = industryDef.tags[subIdx % industryDef.tags.length]
    const productPool =
      industryDef.products[subIdx % industryDef.products.length]

    const location = pick(locations)
    const founded = randInt(1990, 2024)
    const age = 2026 - founded

    const employeeBase = randInt(50, 500)
    const employeeMultiplier = Math.pow(1.3, Math.min(age, 20))
    const employees = Math.round(employeeBase * employeeMultiplier)

    const revRange = pick(revenueRanges)
    const revNum = (
      rand() * (revRange.max - revRange.min) +
      revRange.min
    ).toFixed(1)
    const revenue = `$${revNum}${revRange.unit}`

    const patents = randInt(10, 200) * Math.max(1, Math.floor(age / 3))
    const fundingRound = pick(fundingRoundOptions)
    const totalFunding = pick(fundingRanges)

    const descIdx = randInt(0, industryDef.descriptions.length - 1)
    const description = industryDef.descriptions[descIdx]

    const keyProducts = pickN(productPool, randInt(2, 3))

    const leaderCount = randInt(1, 3)
    const leadership = Array.from({ length: leaderCount }, () => ({
      name: `${pick(lastNames)} ${pick(firstNames)}`,
      title: pick(titles),
    }))

    const innovationScore = randInt(55, 98)
    const website = name.toLowerCase().replace(/\s+/g, '') + '.com'
    const tags = pickN(tagPool, randInt(2, 3))
    const logo = pick(industryDef.logos)

    companies.push({
      id,
      name,
      nameChinese,
      industry: industryDef.industry,
      subIndustry,
      industryCode: generateIndustryCode(
        industryDef.industry,
        subIndustry,
        founded,
        id,
      ),
      city: location.city,
      province: location.province,
      founded,
      employees,
      revenue,
      patents,
      fundingRound,
      totalFunding,
      description,
      keyProducts,
      leadership,
      innovationScore,
      website,
      tags,
      logo,
    })
  }

  return companies
}
